import React, { useEffect, useState, useCallback, memo } from "react";
import axios from "axios";
import "./WebinarPage.css";

// 🔥 GET NEXT MONDAY / FRIDAY 5 PM
const getNextWebinarDate = () => {
  const now = new Date();
  const targetDays = [2, 4];
  let nextDate = new Date(now);

  while (true) {
    const day = nextDate.getDay();

    if (targetDays.includes(day)) {
      const webinarTime = new Date(nextDate);
      webinarTime.setHours(17, 0, 0, 0);

      if (webinarTime > now) {
        return webinarTime;
      }
    }

    nextDate.setDate(nextDate.getDate() + 1);
  }
};

// 🔥 COUNTDOWN COMPONENT (isolated re-render)
const Countdown = memo(({ targetDate, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const diff = targetDate - now;

      if (diff <= 0) {
        onExpire();
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, onExpire]);

  return (
    <div className="countdown-grid">
      <div className="time-box"><h1>{timeLeft.days || "00"}</h1><p>DAYS</p></div>
      <div className="time-box"><h1>{timeLeft.hours || "00"}</h1><p>HOURS</p></div>
      <div className="time-box"><h1>{timeLeft.minutes || "00"}</h1><p>MINUTES</p></div>
      <div className="time-box"><h1>{timeLeft.seconds || "00"}</h1><p>SECONDS</p></div>
    </div>
  );
});

const WebinarPage = () => {

  // 🔥 remove smooth (faster paint)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [latestUsers, setLatestUsers] = useState([]);

  // 🔥 lazy init (important)
  const [targetDate, setTargetDate] = useState(() => getNextWebinarDate());

  // ✅ optimized handler
  const handleChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await axios.post("/api/webinar-register", {
        ...formData,
        webinarDate: targetDate
      });

      if (res.status === 200) {
        alert("✅ Registered Successfully!");
        setShowForm(false);
        setFormData({ name: "", email: "", phone: "" });
      }

    } catch (error) {
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert("Something went wrong");
      }
    }
  };

  // 🔥 optimized expire handler
  const handleExpire = useCallback(() => {
    setTargetDate(getNextWebinarDate());
  }, []);

  // 🔥 optimized API polling
  useEffect(() => {
    let interval;

    const fetchLatest = async () => {
      try {
        const res = await axios.get("/api/webinar-users/latest", {
          params: { webinarDate: targetDate.toISOString() }
        });

        setLatestUsers(res.data.users);
      } catch (err) {
        console.error(err);
      }
    };

    fetchLatest();

    const startPolling = () => {
      interval = setInterval(fetchLatest, 15000); // 🔥 reduced
    };

    startPolling();

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [targetDate]);

  return (
    <div className="webinar-container">

      {/* HERO */}
      <div className="hero">
        <div className="left">
          <span className="badge">🔥 Free Live Webinar</span>

          <h1>
            Why Genetics Isn't Your Real Problem (The 3 Trainable Skills That Separate National Team Players From Everyone Else)
          </h1>

          <p>Learn how vision, reaction speed, and brain-based training improve performance.</p>

          <div className="tags">
            <span>🎯 Athletes, Coaches & Parents</span>
            <span>⏱ 90 Minutes</span>
            <span>💻 Online Event</span>
          </div>

          <div className="buttons">
            <button className="btn red" onClick={() => setShowForm(true)}>
              Reserve Your Seat
            </button>
          </div>
        </div>

        <div className="right">
          <h3>NEXT WEBINAR</h3>
          <h2>
            {targetDate.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            })}
          </h2>
          <h2>5:00 PM IST</h2>

          <p>Limited seats. Reserve early.</p>

          <button className="btn red" onClick={() => setShowForm(true)}>
            Reserve Now
          </button>
        </div>
      </div>

      {/* COUNTDOWN */}
      <div className="countdown-wrapper">
        <h2>Countdown to the Webinar</h2>
        <p className="subtitle">The next live session begins soon.</p>

        <Countdown targetDate={targetDate} onExpire={handleExpire} />

        <button className="btn red" onClick={() => setShowForm(true)}>
          Reserve My Seat
        </button>

        <p className="live-join-text">
          {latestUsers.slice(0, 2).map((user, i) => (
            <span key={i}>
              🔥 {user.name} just joined <br />
            </span>
          ))}
        </p>
      </div>

      {/* CARDS */}
      <div className="cards">
        <div className="card">
          <h3>Why Attend?</h3>
          <p>Understand how visual clarity, reaction speed, and cognitive processing influence real match performance.</p>
        </div>

        <div className="card">
          <h3>Who Is This For?</h3>
          <p>Athletes who want an edge, coaches who want deeper insight, and parents who want to understand performance better.</p>
        </div>

        <div className="card">
          <h3>What Makes It Different?</h3>
          <p>This is not generic motivation. It is a practical performance framework built around measurable human potential.</p>
        </div>
      </div>

      {/* CTA */}
      <div className="cta">
        <h2>Reserve Your Seat for the Next Webinar</h2>
        <p>
          If you want to understand the real foundations of athletic performance — from visual input to decision output — this webinar is the best place to start.
        </p>
        <button className="btn red" onClick={() => setShowForm(true)}>
          Reserve My Seat
        </button>
      </div>

      {/* POPUP */}
      {showForm && (
        <div className="popup-overlay">
          <div className="popup-form">
            <h2>Reserve Your Seat</h2>

            
              <form onSubmit={handleSubmit}>

                <label>Name</label>
                <input 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                />

                <label>Email</label>
                <input 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />

                <label>Phone</label>
                <input 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                />

                <button className="btn red full">Submit</button>
              </form>

              <button className="close-btn" onClick={() => setShowForm(false)}>✖</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default WebinarPage;